<script lang="ts">
    import { onDestroy } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let value: number = 0;
    export let min: number = -Infinity;
    export let max: number = Infinity;
    export let step: number = 1;
    export let disabled: boolean = false;
    export let required: boolean = false;
    export let unit: string = '';

    let touched = false;
    let inputEl: HTMLInputElement | null = null;
    errors[name] = [];

    const validate = (val: number = value) => {
        const errs: string[] = [];
        if (inputEl?.validity.badInput || isNaN(val)) errs.push('Enter a valid number');
        else if (required && val === null) errs.push('This field is required');
        else if (val < min) errs.push(`Minimum value is ${min}`);
        else if (val > max) errs.push(`Maximum value is ${max}`);
        errors[name] = errs;
    };
    validate();

    const handleInput = (e: Event) => {
        const el = e.currentTarget as HTMLInputElement;
        value = el.value === '' ? NaN : parseFloat(el.value);
        if (touched) validate(value);
    };

    const handleBlur = (e: FocusEvent) => {
        touched = true;
        const el = e.currentTarget as HTMLInputElement;
        value = el.value === '' ? NaN : parseFloat(el.value);
        validate(value);
    };

    const decrement = () => { if (!disabled && value - step >= min) { value -= step; validate(value); } };
    const increment = () => { if (!disabled && value + step <= max) { value += step; validate(value); } };

    onDestroy(() => { errors[name] = []; });
</script>

<div class="form-field">
    {#if label}<span class="label-text">{label}</span>{/if}
    <div class="stepper" class:error={errors[name].length > 0}>
        <button type="button" class="stepper-btn" on:click={decrement} {disabled} aria-label="Decrease">−</button>
        <div class="stepper-input-wrap">
            <input
                type="number"
                {name}
                {disabled}
                {required}
                {min}
                {max}
                {step}
                value={value}
                bind:this={inputEl}
                on:input={handleInput}
                on:blur={handleBlur}
                on:change
            />
            {#if unit}<span class="stepper-unit">{unit}</span>{/if}
        </div>
        <button type="button" class="stepper-btn" on:click={increment} {disabled} aria-label="Increase">+</button>
    </div>
    <span class="field-errors">
        {#each errors[name] as err}<span>{err}</span>{/each}
    </span>
</div>

<style lang="scss">
    .stepper {
        display: flex;
        align-items: stretch;
        height: 44px;
        border: 1px solid $color-border;
        border-radius: $border-radius-medium;
        overflow: hidden;
        background-color: transparent;
        transition: all 0.2s ease;

        &:hover:not(.disabled) {
            border-color: $color-text;
            background-color: rgba(128, 128, 128, 0.03);
        }

        &:focus-within {
            border: 2px solid $color-primary;
            .stepper-btn, .stepper-input-wrap {
                margin-top: -1px;
                margin-bottom: -1px;
            }
            .stepper-btn:first-child { margin-left: -1px; }
            .stepper-btn:last-child { margin-right: -1px; }
        }

        &.error {
            border-color: $color-danger !important;
        }
    }

    .stepper-input-wrap {
        flex: 1;
        display: flex;
        align-items: center;
        background-color: transparent;
        min-width: 0;

        input {
            flex: 1;
            text-align: center;
            padding: 0 0.5em;
            font-family: inherit;
            font-size: $font-size;
            color: $color-text;
            width: 100%;
            min-width: 2em;
            appearance: textfield;
            -moz-appearance: textfield;
            border: none !important;
            border-radius: 0 !important;

            &:hover:not(:disabled) {
                border-color: $color-text;
                background-color: transparent;
            }

            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
        }
    }

    .stepper-btn {
        all: unset;
        flex-shrink: 0;
        width: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.4em;
        font-weight: 400;
        background-color: $color-bg-active;
        color: $color-text;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover:not(:disabled) {
            background-color: $color-border;
        }
        &:active:not(:disabled) {
            background-color: $color-bg-active;
        }
        &:disabled {
            opacity: 0.3;
            cursor: default;
        }

        &:first-child {
            border-right: 1px solid $color-border;
        }
        &:last-child {
            border-left: 1px solid $color-border;
        }
    }

    .stepper-unit {
        padding-right: 1em;
        font-size: 0.9em;
        font-weight: 600;
        color: $color-text-secondary;
        white-space: nowrap;
        pointer-events: none;
    }
</style>
